import {memo, useState} from 'react';
import PropTypes from 'prop-types';
import {checklistShape} from '../shapes';
import ChecklistItem from './ChecklistItem';

const Checklist = memo(function Checklist({data, onDelete, onAddItem, onToggleItem, onDeleteItem, onUpdateTitle, onUpdateItem, isFocused, onFocus, onUnfocus}) {
    const [newItemTitle, setNewItemTitle] = useState('');
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');

    function handleAdd() {
        const title = newItemTitle.trim();
        if (!title) return;
        onAddItem(data.id, title);
        setNewItemTitle('');
    }

    function startEditingTitle() {
        setTitleDraft(data.title);
        setEditingTitle(true);
    }

    function commitTitle() {
        const title = titleDraft.trim();
        if (title && title !== data.title) onUpdateTitle(data.id, title);
        setEditingTitle(false);
    }

    function handleTitleKeyDown(e) {
        if (e.key === 'Enter') commitTitle();
        if (e.key === 'Escape') setEditingTitle(false);
    }

    const checked = data.items.filter(i => i.checked).length;
    const total = data.items.length;
    const pct = total > 0 ? Math.round((checked / total) * 100) : 0;

    return (
        <div className={`checklist${isFocused ? ' checklist--focused' : ''}`}>
            <div className="checklist-sticky">
            <div className="checklist-header">
                {editingTitle
                    ? <input
                        className="checklist-title-input"
                        value={titleDraft}
                        onChange={e => setTitleDraft(e.target.value)}
                        onBlur={commitTitle}
                        onKeyDown={handleTitleKeyDown}
                        autoFocus
                      />
                    : <h2
                        onClick={startEditingTitle}
                        onKeyDown={e => e.key === 'Enter' && startEditingTitle()}
                        tabIndex={0}
                      >{data.title}</h2>
                }
                <div className="checklist-header-actions">
                    <button
                        className="checklist-focus-btn"
                        onClick={() => isFocused ? onUnfocus() : onFocus(data.id)}
                        aria-label={isFocused ? 'Exit focus mode' : 'Focus checklist'}
                    >
                        {isFocused
                            ? <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <polyline points="4,1 1,1 1,4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="8,1 11,1 11,4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="11,8 11,11 8,11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="4,11 1,11 1,8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            : <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <polyline points="1,4 1,1 4,1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="11,4 11,1 8,1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="8,11 11,11 11,8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="1,8 1,11 4,11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                        }
                    </button>
                    <button onClick={() => onDelete(data.id)} aria-label="Delete checklist">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="1,3 11,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <path d="M4,3 L4,2 Q4,1 5,1 L7,1 Q8,1 8,2 L8,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2.5,3 L3,10 Q3,11 4,11 L8,11 Q9,11 9,10 L9.5,3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
                {total > 0 && (
                    <div className="checklist-header-progress">
                        <div className="checklist-header-progress-fill" style={{width: `${pct}%`}} />
                    </div>
                )}
            </div>
            <div className="checklist-add-item">
                <div className="checklist-add-item-inner">
                    <input
                        type="text"
                        placeholder="Add new item"
                        value={newItemTitle}
                        onChange={e => setNewItemTitle(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAdd()}
                    />
                    <button onClick={handleAdd}>Add</button>
                </div>
            </div>
            </div>
            {data.items.map(item => (
                <ChecklistItem
                    key={item.id}
                    checklistId={data.id}
                    item={item}
                    onToggle={onToggleItem}
                    onDelete={onDeleteItem}
                    onUpdate={onUpdateItem}
                />
            ))}
        </div>
    );
});

Checklist.propTypes = {
    data: checklistShape.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAddItem: PropTypes.func.isRequired,
    onToggleItem: PropTypes.func.isRequired,
    onDeleteItem: PropTypes.func.isRequired,
    onUpdateTitle: PropTypes.func.isRequired,
    onUpdateItem: PropTypes.func.isRequired,
    isFocused: PropTypes.bool,
    onFocus: PropTypes.func.isRequired,
    onUnfocus: PropTypes.func.isRequired,
};

export default Checklist;
